import React, { useState } from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import Delete from '@mui/icons-material/Delete';
import { Link } from 'react-router-dom';
import moment from 'moment';
import qs from 'query-string';
import { deleteTacheContact, getAllMesTaches } from '../../../services/TacheContactService';
import LabelHtmlToTextCustom from '../../../components/LabelHtmlToTextCustom/LabelHtmlToTextCustom';
import DialogDeleteCustom from '../../../components/DialogDeleteCustom/DialogDeleteCustom';
import { withSnackbar } from '../../../utils/SnackbarHOC';
import { commonGridSx, gridContainerSx, gridLocaleText, makeGridToolbar } from '../../../utils/gridStyles';
import { DataGrid } from '../../../components/SortableDataGrid/SortableDataGrid';

const CustomToolbar = makeGridToolbar('Tâches Contacts');

const statusConfig = {
  'à faire': { bg: '#fef9c3', color: '#854d0e', border: '#fde047' },
  'en cours': { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
  'terminée': { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  'annulée': { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' },
};

function ContactTasksTable({ snackbarShowMessage, searchParams, reloadingData, setReloadingData }) {
  const [loading, setLoading] = useState(false);
  const [tachesContact, setTachesContact] = useState([]);
  const [tacheContactInfo, setTacheContactInfo] = useState({ commentaire: '', tacheId: '' });
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [loadingToDelete, setLoadingToDelete] = useState(false);
  const [page, setPage] = useState(1);

  React.useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setTachesContact([]);
      try {
        const res = await getAllMesTaches(page, qs.stringify(searchParams));
        if (!active) return;
        setTachesContact(res.data.mesTachesContact || []);
      } catch (error) {
        snackbarShowMessage('Erreur de chargement des tâches contact: ' + error.message, 'error', 4000);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [page, reloadingData]);

  const handleOpenDialog = (row) => {
    setTacheContactInfo({ commentaire: row.commentaire, tacheId: row.id });
    setOpenDialogDelete(true);
  };

  const handleCloseDialog = () => { if (!loadingToDelete) setOpenDialogDelete(false); };

  const handleDeleteTask = async () => {
    setLoadingToDelete(true);
    try {
      snackbarShowMessage('Patientez svp!', 'warning', null);
      await deleteTacheContact(tacheContactInfo.tacheId);
      snackbarShowMessage('Tâche du Contact bien supprimée!', 'success', 4000);
      handleCloseDialog();
      setReloadingData(!reloadingData);
    } catch (error) {
      snackbarShowMessage('Erreur de suppression de la données: ' + error.message, 'error', 4000);
    } finally {
      setLoadingToDelete(false);
    }
  };

  const columns = [
    {
      field: 'createdAt', headerName: 'CRÉÉ LE', flex: 120, minWidth: 120,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>
          {p.value ? moment(p.value).format('DD/MM/YYYY') : '—'}
        </Typography>
      ),
    },
    {
      field: 'societe', headerName: 'SOCIÉTÉ', flex: 160, minWidth: 160,
      renderCell: (p) => p.value ? (
        <Link to={`/societe/${p.row.societeId}/view`}
          style={{ textDecoration: 'none', color: '#0ea5e9', fontSize: '1rem' }}>
          {p.value}
        </Link>
      ) : <Typography sx={{ color: '#cbd5e1', fontSize: '1rem' }}>—</Typography>,
    },
    {
      field: 'status', headerName: 'STATUT', flex: 120, minWidth: 120,
      renderCell: (p) => {
        const key = (p.value || '').toLowerCase();
        const cfg = statusConfig[key] || { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' };
        return (
          <Chip label={p.value || '—'} size="small"
            sx={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, fontWeight: 600, fontSize: '1rem', height: 22 }} />
        );
      },
    },
    {
      field: 'contact', headerName: 'CLIENT', flex: 160, minWidth: 160,
      renderCell: (p) => p.value ? (
        <Link to={`/societe/${p.row.societeId}/contacts/${p.row.contactId}/view`}
          style={{ textDecoration: 'none', color: '#0ea5e9', fontSize: '1rem' }}>
          {p.value}
        </Link>
      ) : <Typography sx={{ color: '#cbd5e1', fontSize: '1rem' }}>—</Typography>,
    },
    {
      field: 'fonction', headerName: 'FONCTION', flex: 170, minWidth: 170,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'commentaire', headerName: 'NOTE', flex: 220, minWidth: 220,
      renderCell: (p) => p.value ? (
        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 210 }}>
          <LabelHtmlToTextCustom data={p.value} />
        </Box>
      ) : <Typography sx={{ color: '#cbd5e1', fontSize: '1rem' }}>—</Typography>,
    },
    {
      field: 'dateTime', headerName: 'DATE RÉALISÉE', flex: 130, minWidth: 130,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>
          {p.value ? moment(p.value).format('DD/MM/YYYY') : '—'}
        </Typography>
      ),
    },
    {
      field: 'actions_col', headerName: 'ACTIONS', width: 130, sortable: false, filterable: false,
      renderCell: (p) => (
        <Tooltip title="Supprimer">
          <IconButton size="small" onClick={() => handleOpenDialog(p.row)}
            sx={{ color: '#ef4444', background: '#fef2f2', borderRadius: '8px', '&:hover': { background: '#fee2e2' }, p: 0.75 }}>
            <Delete sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const rows = (tachesContact || []).map((row) => ({
    ...row,
    id: row.id,
    createdAt: row.createdAt?.date ? moment(row.createdAt.date).toDate().getTime() : row.createdAt,
    dateTime: row.dateTime?.date ? moment(row.dateTime.date).toDate().getTime() : row.dateTime,
  }));

  return (
    <>
      <Box sx={{ ...gridContainerSx, height: 'calc(100vh - 240px)' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          pageSizeOptions={[50, 100]}
          disableRowSelectionOnClick
          showToolbar
          slots={{ toolbar: CustomToolbar }}
          slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
          localeText={gridLocaleText}
          sx={commonGridSx}
        />
      </Box>
      <DialogDeleteCustom
        handleClose={handleCloseDialog}
        open={openDialogDelete}
        handleConfirm={handleDeleteTask}
        loading={loadingToDelete}
        label="Voulez vous vraiment supprimer cette tâche ?"
        title="Supprimer la tâche"
      />
    </>
  );
}

export default withSnackbar(ContactTasksTable);

